import * as React from "react";
import { createPortal } from "react-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faXmark } from "@fortawesome/pro-light-svg-icons";
import { cn } from "@core/lib/utils";
import { Button } from "@core/components/ui/button";
import Spinner from "@core/components/ui/Spinner";

export type SubmissionOverlayState = "idle" | "submitting" | "success";

interface SubmissionOverlayProps extends React.HTMLAttributes<HTMLDivElement> {
  state: SubmissionOverlayState;
  successTitle?: React.ReactNode;
  successDescription?: React.ReactNode;
  onClose?: () => void;
  closeLabel?: React.ReactNode;
}

export const SubmissionOverlay = ({
  state,
  successTitle,
  successDescription,
  onClose,
  closeLabel,
  className,
  ...props
}: SubmissionOverlayProps) => {
  if (state === "idle") return null;

  const content =
    state === "submitting" ? (
      <div
        className={cn(
          "fixed inset-0 z-[9999] bg-black/40 flex items-center justify-center pointer-events-auto",
          className
        )}
        {...props}
      >
        <div className="bg-white dark:bg-neutral-900 rounded-lg shadow-lg px-6 py-4 flex items-center gap-3 border border-neutral-200 dark:border-neutral-700">
          <Spinner />
          <div className="text-sm font-medium text-slate-800 dark:text-neutral-200">
            Submitting payment…
          </div>
        </div>
      </div>
    ) : (
      <div
        className={cn(
          "fixed inset-0 z-[9999] bg-black/40 flex items-center justify-center p-4 pointer-events-auto",
          className
        )}
        {...props}
      >
        <div className="relative w-full max-w-md bg-white dark:bg-neutral-900 rounded-lg shadow-lg border border-neutral-200 dark:border-neutral-700 p-6">
          {onClose ? (
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              className="absolute right-3 top-3 rounded-sm p-1 text-neutral-500 transition-colors hover:text-neutral-900 focus:outline-none focus:ring-2 focus:ring-neutral-400 dark:text-neutral-400 dark:hover:text-neutral-100"
            >
              <FontAwesomeIcon icon={faXmark} className="h-4 w-4" />
            </button>
          ) : null}
          <p className="font-medium text-neutral-900 dark:text-neutral-100">
            {successTitle ?? "Payment applied successfully"}
          </p>
          {successDescription ? (
            <p className="mt-1 text-sm text-neutral-600 dark:text-neutral-400">
              {successDescription}
            </p>
          ) : null}
          {onClose ? (
            <div className="mt-6 flex justify-end">
              <Button type="button" variant="primary" onClick={onClose}>
                {closeLabel ?? "Close"}
              </Button>
            </div>
          ) : null}
        </div>
      </div>
    );

  if (typeof document !== "undefined") {
    return createPortal(content, document.body);
  }

  return content;
};
